import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  faCalendarDays,
  faCircleMinus,
  faCirclePlus,
  faInfinity,
  faPrint,
  faUserGraduate,
  faUserGroup,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';

import * as _ from 'underscore';

import { AssignDiagnosticService, AssignDiagnosticsResolvedData } from '../../core/services/assign-diagnostic.service';
import { SidebarService } from '../../core/services/sidebar.service';
import { SubscriptionTypes } from '../../core/models/subscription-types.model';
import { Student } from '../../core/models/student.model' ;
import { StudentsService } from '../../core/services/students.service';
import { SchoolClass } from 'src/app/core/models/school-class.model';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { PrintService } from '../../core/services/print.service';
import { StudentsFilterComponent } from '../../shared/components/students-filter/students-filter.component';

import introJs from 'intro.js';
import { manageSubscriptionsIntroSteps } from './manage-subscriptions.intro-steps';
import { SchoolAdminTeacherFilterService } from 'src/app/shared/components/school-admin-teacher-filter/school-admin-teacher-filter.service';

@Component({
    selector: 'wf-assign-diagnostics',
    templateUrl: './assign-diagnostics.component.html',
    styles: [],
    standalone: false
})
export class AssignDiagnosticsComponent implements OnInit {
  UNLIMITED_LICENSE: number = -1 ;
  showConfirmationDialog: boolean = false;
  showSchedulingButton: boolean = false;
  orderByField: any = null;
  reverseSort: boolean = false;
  fullProduct: string = SubscriptionTypes.FullProduct;
  diagnosticProduct:string = SubscriptionTypes.DiagnosticProduct;
  drawerIsOpen = false;
  openDrawer = this.sidebarService.toggleDrawer();
  numberOfFullProductLicenses: string | number = 0 ;
  numberOfDiagnosticProductLicenses: string | number = 0;
  grades: string[] = [] ;
  classes: SchoolClass[] = [] ;
  students: Student[] = [];
  filteredStudents: Student[] = [];
  teacherNamesById: Record<number, string> = {};
  teacherLicenseAvailabilityById: Record<number, { diagnosticLicenseCount: number, interventionLicenseCount: number }> = {};
  currentFilters: any = {};
  showTeacherFilter: boolean = false;
  canCustomizeDiagnosticSchedule: boolean = false;
  isLoadingLicenses: boolean = false;
  studentSummaryData: { [studentId: number]: any } = {};
  printIcon = faPrint ;
  removeDiagnosticIcon = faCircleMinus ;
  addDiagnosticIcon = faCirclePlus ;
  infinityIcon = faInfinity ;
  customizeIcon = faCalendarDays ;
  studentIcon = faUserGraduate ;
  enrollIcon = faUserGroup ;
  helpIcon = faQuestionCircle ;
  introTour: any;

  @ViewChild('printContent') printContent!: ElementRef<HTMLElement> ;
  @ViewChild(StudentsFilterComponent) studentFilter?: StudentsFilterComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sidebarService: SidebarService,
    private studentService: StudentsService,
    private sessionStorageService: SessionStorageService,
    private printService: PrintService,
    private assignDiagnosticService: AssignDiagnosticService,
    private teacherFilterService: SchoolAdminTeacherFilterService,
  ) { }

  ngOnInit(): void {
    const resolvedData: AssignDiagnosticsResolvedData = this.route.snapshot.data['resolveData'];
    const currentUser = this.sessionStorageService.userData();
    this.showTeacherFilter = currentUser?.isSchoolAdmin() ?? false;
    this.canCustomizeDiagnosticSchedule = currentUser?.isTeacherUser() ?? false;
    this.numberOfDiagnosticProductLicenses = resolvedData.diagnosticLicenseCount;
    this.numberOfFullProductLicenses = resolvedData.interventionLicenseCount;
    this.students = resolvedData.students;
    this.filteredStudents = this.students ;
    this.grades = resolvedData.grades;
    this.classes = resolvedData.classes;
    this.teacherNamesById = this.teacherFilterService.getTeacherNamesById();
    this.teacherLicenseAvailabilityById = resolvedData.teacherLicenseAvailabilityById || {};
  }

  ngAfterViewInit(): void {
    this.setupIntroTour();
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }

      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  setupIntroTour() {
    this.introTour = introJs();
    this.introTour.setOptions({
      steps: manageSubscriptionsIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
      showBullets: true,
      showSkip: false,
    });
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(manageSubscriptionsIntroSteps);
    this.introTour.setOptions({ steps: filteredSteps });
    this.introTour.start();
  }

  sortTable(columnName: string) {
    if (this.orderByField === columnName)
    {
      this.reverseSort = !this.reverseSort ;
    }
    this.orderByField = columnName;

    this.filteredStudents = (this.reverseSort) ? _.sortBy(this.filteredStudents, columnName).reverse() : _.sortBy(this.filteredStudents, columnName) ;
  }

  assignDiagnosticSubscription(student: Student) {
    if (this.hasDiagnosticLicensesForStudent(student))
    {
       this.assignDiagnosticService.assignDiagnosticToStudent(student.userID, this.getTeacherIdForAssignment(student)).subscribe({
          next: () => {
             this.reloadCurrentTeacherData(true);
           }
        });
    }
  }

  removeDiagnosticSubscription(student: Student, subscriptionID: number) {
    this.assignDiagnosticService.removeDiagnosticFromStudent(student.userID, subscriptionID).subscribe({
      next: () => {
        this.reloadCurrentTeacherData(true);
      }
    })
  }

  assignFullProductSubscription(student: Student) {
    if (this.hasInterventionLicensesForStudent(student))
    {
      this.assignDiagnosticService.assignFullProductToStudent(student.userID, this.getTeacherIdForAssignment(student)).subscribe({
        next: () => {
          this.reloadCurrentTeacherData(true);
        }
      }) ;
    }
  }

  removeFullProductSubscription(student: Student, subscriptionID: number) {
    this.assignDiagnosticService.removeFullProductFromStudent(student.userID, subscriptionID).subscribe({
      next: () => {
        this.reloadCurrentTeacherData(true);
      }
    })
  }

  showAddTestButton(student: Student, lastRow: any) {
    if (this.isLoadingLicenses) {
      return false;
    }

    return (lastRow && student.showAddTestButton && this.hasDiagnosticLicensesForStudent(student));
  }

  showRemoveTestButton(assessment: any) {
    return (assessment != null) && (assessment.state === 'Assigned') && assessment.isDiagnostic;
  }

  showAddFullProductButton(student: Student, assessment: any, lastRow: any) {
    if (this.isLoadingLicenses) {
      return false;
    }

    let showButton = false;
    if ((assessment != null) && this.hasInterventionLicensesForStudent(student)) {
      showButton = lastRow && ((assessment.state === 'Unassigned') || assessment.expired || (assessment.state === 'Completed' && assessment.isDiagnostic));
    }
    return showButton;
  }

  showRemoveFullProductButton(student: any, assessment: any) {
    return (assessment != null) && (student.showRemoveACButton) && (assessment.isPreDiagnostic);
  }

  goToScheduleView() {
    this.router.navigateByUrl('/students/manage-subscriptions/scheduling');
  }

  toggleConfirmation() {
    this.showConfirmationDialog = !this.showConfirmationDialog;
  }

  checkForConfirmation() {
    this.showConfirmationDialog = true;
  }

  printStudents() {
    const userData = this.sessionStorageService.userData();
    let selectedTeacherName = `${userData?.firstName} ${userData?.lastName}`;

    // If we are viewing the student list for a teacher other than the current user, then we need to
    // get the teacher name from the session storage
    if (this.showTeacherFilter) {
      const filteredTeacherName = this.teacherFilterService.getSelectedTeacherName();
      if (filteredTeacherName != null) {
        selectedTeacherName = filteredTeacherName;
      }
    }
    this.printService.openPrintWindow(this.printContent.nativeElement, userData?.school || '', selectedTeacherName);
  }

  filterStudents(filterOpts: any) {
    this.currentFilters = filterOpts;
    this.filteredStudents = this.studentService.filterStudents(this.students, filterOpts) ;
  }

  onTeacherFilterChange() {
    this.reloadCurrentTeacherData(true);
  }

  shouldShowSubscriptionCounts(): boolean {
    return !this.isShowingAllTeachers();
  }

  isShowingAllTeachers(): boolean {
    return this.showTeacherFilter && this.teacherFilterService.getSelectedTeacherId() === 0;
  }

  getTeacherNameForStudent(student: Student): string {
    const teacherId = student.teacherID || student.schoolClass?.teacherID;
    return student.teacher || (teacherId ? this.teacherNamesById[teacherId] : '') || '';
  }

  getTeacherLicenseSummaryForStudent(student: Student): string {
    const teacherId = student.teacherID || student.schoolClass?.teacherID;
    if (!teacherId) {
      return '';
    }

    const availability = this.teacherLicenseAvailabilityById[teacherId];
    if (!availability) {
      return '';
    }

    return `Diagnostic: ${this.formatLicenseCount(availability.diagnosticLicenseCount)} | System: ${this.formatLicenseCount(availability.interventionLicenseCount)}`;
  }

  private getTeacherIdForAssignment(student?: Student): number | undefined {
    if (this.isShowingAllTeachers()) {
      return student?.teacherID || student?.schoolClass?.teacherID || undefined;
    }

    if (this.showTeacherFilter) {
      return this.teacherFilterService.getSelectedTeacherId() ?? undefined;
    }

    return undefined;
  }

  private hasDiagnosticLicensesForStudent(student: Student): boolean {
    return this.hasLicensesAvailable(this.getDiagnosticLicensesForStudent(student));
  }

  private hasInterventionLicensesForStudent(student: Student): boolean {
    return this.hasLicensesAvailable(this.getInterventionLicensesForStudent(student));
  }

  private hasLicensesAvailable(licenseCount: number | string): boolean {
    const normalizedCount = parseInt(licenseCount.toString(), 10);
    return normalizedCount > 0 || normalizedCount === this.UNLIMITED_LICENSE;
  }

  private formatLicenseCount(licenseCount: number): string {
    return licenseCount === this.UNLIMITED_LICENSE ? 'Unlimited' : licenseCount.toString();
  }

  private getDiagnosticLicensesForStudent(student: Student): number | string {
    if (!this.isShowingAllTeachers()) {
      return this.numberOfDiagnosticProductLicenses;
    }

    const teacherId = student.teacherID || student.schoolClass?.teacherID;
    if (!teacherId) {
      return 0;
    }

    return this.teacherLicenseAvailabilityById[teacherId]?.diagnosticLicenseCount ?? 0;
  }

  private getInterventionLicensesForStudent(student: Student): number | string {
    if (!this.isShowingAllTeachers()) {
      return this.numberOfFullProductLicenses;
    }

    const teacherId = student.teacherID || student.schoolClass?.teacherID;
    if (!teacherId) {
      return 0;
    }

    return this.teacherLicenseAvailabilityById[teacherId]?.interventionLicenseCount ?? 0;
  }

  private reloadCurrentTeacherData(resetFilters: boolean = false) {
    const currentUser = this.sessionStorageService.userData();
    if (!currentUser) {
      return;
    }

    const selectedTeacherId = this.showTeacherFilter
      ? (this.teacherFilterService.getSelectedTeacherId() ?? currentUser.userID)
      : currentUser.userID;
    this.isLoadingLicenses = true;

    this.assignDiagnosticService.getTeacherPageData({
      teacherID: selectedTeacherId,
      schoolID: currentUser.schoolID}
    ).subscribe({
      next: (pageData) => {
        this.numberOfDiagnosticProductLicenses = pageData.diagnosticLicenseCount;
        this.numberOfFullProductLicenses = pageData.interventionLicenseCount;
        this.students = pageData.students;
        this.grades = pageData.grades;
        this.classes = pageData.classes;
        this.teacherNamesById = this.teacherFilterService.getTeacherNamesById();
        this.teacherLicenseAvailabilityById = pageData.teacherLicenseAvailabilityById || {};

        if (resetFilters) {
          this.currentFilters = {
            grade: 'all',
            class: '0',
            extraSupport: 'all'
          };

          if (this.studentFilter) {
            this.studentFilter.resetFilterSettings();
            this.currentFilters = this.studentFilter.getFilterSettings();
          }
        }

        this.filteredStudents = this.studentService.filterStudents(this.students, this.currentFilters);
      },
      complete: () => {
        this.isLoadingLicenses = false;
      },
      error: () => {
        this.isLoadingLicenses = false;
      }
    });
  }
}
